// Cloud backup / restore of the whole POS dataset to a Supabase project.
//
// The entire app state lives in IndexedDB under the `sconnect.` prefix (see
// services/db.ts). This module serialises every such key into one JSON blob and
// stores it as a row in the Supabase `pos_backups` table, keyed by a
// user-chosen "backup key" (think of it as the store/account identifier). Any
// terminal that knows the same backup key can pull the latest snapshot back
// down, so a shop can move data between devices or recover after a reinstall.
//
// We talk to Supabase over its PostgREST HTTP API with plain `fetch` rather
// than the @supabase/supabase-js SDK, to avoid adding an npm dependency.
//
// One-time setup (run in the Supabase dashboard → SQL editor):
//
//   create table if not exists public.pos_backups (
//     id         uuid primary key default gen_random_uuid(),
//     store_key  text not null,
//     data       jsonb not null,
//     created_at timestamptz not null default now()
//   );
//   create index if not exists pos_backups_key_time
//     on public.pos_backups (store_key, created_at desc);
//   alter table public.pos_backups enable row level security;
//   create policy "pos_backups anon access" on public.pos_backups
//     for all to anon using (true) with check (true);
//
// Note: with the publishable (anon) key + the permissive policy above, anyone
// who knows your project URL *and* a store_key can read/write that store's
// backups. Treat the backup key like a password.

import { REST, sbHeaders, sbError } from './supabase';
import { idbAllKeys, idbGetRaw, idbSetRaw, idbDel, cachedRawKey } from './idb';

const TABLE = 'pos_backups';

// Where the chosen backup key is remembered. Kept OUTSIDE the `sconnect.`
// namespace so restoring a snapshot doesn't clobber the key used to fetch it.
const backupKeyCache = cachedRawKey('epos.cloudBackupKey', '');

const PREFIX = 'sconnect.';

export interface CloudBackupMeta {
  id: string;
  created_at: string;
}

export function getBackupKey(): string {
  return backupKeyCache.get();
}

export function setBackupKey(key: string): void {
  backupKeyCache.set(key.trim());
}

// Snapshot every POS collection currently stored.
async function collectLocalData(): Promise<Record<string, unknown>> {
  const dump: Record<string, unknown> = {};
  for (const key of await idbAllKeys()) {
    if (!key.startsWith(PREFIX)) continue;
    const raw = await idbGetRaw(key);
    if (raw == null) continue;
    try {
      dump[key] = JSON.parse(raw);
    } catch {
      /* skip non-JSON values */
    }
  }
  return dump;
}

// Overwrite local POS collections with a downloaded snapshot. Existing
// `sconnect.` keys not present in the snapshot are removed so the local state
// matches the backup exactly.
async function applyLocalData(data: Record<string, unknown>): Promise<void> {
  for (const k of await idbAllKeys()) {
    if (k.startsWith(PREFIX)) await idbDel(k);
  }
  for (const [k, v] of Object.entries(data)) {
    if (k.startsWith(PREFIX)) await idbSetRaw(k, JSON.stringify(v));
  }
}

async function fail(res: Response): Promise<never> {
  const err = await sbError(res);
  if (res.status === 404 && /pos_backups/.test(err.message)) {
    throw new Error(
      'Backup table not found. Create the "pos_backups" table in Supabase first (see setup SQL in cloudBackup.ts).'
    );
  }
  throw err;
}

// Upload the current dataset as a new snapshot row for `storeKey`.
export async function backupToCloud(storeKey: string): Promise<CloudBackupMeta> {
  const key = storeKey.trim();
  if (!key) throw new Error('Enter a backup key first.');
  const res = await fetch(`${REST}/${TABLE}`, {
    method: 'POST',
    headers: sbHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify({ store_key: key, data: await collectLocalData() }),
  });
  if (!res.ok) await fail(res);
  const rows = (await res.json()) as CloudBackupMeta[];
  return rows[0];
}

// Most recent snapshot's metadata for `storeKey`, or null if none exist.
export async function getLatestBackupMeta(storeKey: string): Promise<CloudBackupMeta | null> {
  const key = storeKey.trim();
  if (!key) return null;
  const url =
    `${REST}/${TABLE}` +
    `?select=id,created_at&store_key=eq.${encodeURIComponent(key)}` +
    `&order=created_at.desc&limit=1`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) await fail(res);
  const rows = (await res.json()) as CloudBackupMeta[];
  return rows[0] ?? null;
}

// Download the latest snapshot for `storeKey` and apply it locally. Returns the
// restored snapshot's metadata. Throws if there is no backup for that key.
export async function restoreFromCloud(storeKey: string): Promise<CloudBackupMeta> {
  const key = storeKey.trim();
  if (!key) throw new Error('Enter a backup key first.');
  const url =
    `${REST}/${TABLE}` +
    `?select=id,created_at,data&store_key=eq.${encodeURIComponent(key)}` +
    `&order=created_at.desc&limit=1`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) await fail(res);
  const rows = (await res.json()) as Array<CloudBackupMeta & { data: Record<string, unknown> }>;
  if (rows.length === 0) throw new Error(`No cloud backup found for key "${key}".`);
  await applyLocalData(rows[0].data);
  return { id: rows[0].id, created_at: rows[0].created_at };
}
